import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Space, 
  Spin, 
  Alert,
  Progress,
  Tooltip,
  Typography,
  Dropdown,
  Menu,
  message
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined
} from '@ant-design/icons';
import {
  getInventoryStats,
  subscribeToInventoryItems,
  deleteInventoryItem,
  INVENTORY_STATUS,
  INVENTORY_STATUS_LABELS,
  INVENTORY_STATUS_COLORS,
  MATERIAL_TYPES
} from '../../firebase/inventoryService';
import InventoryForm from './InventoryForm';
import ScaffoldAvailabilitySelector from './ScaffoldAvailabilitySelector';
import './InventoryDashboard.css';

const { Title, Text } = Typography;
const { Option } = Select;

const InventoryDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [materialFilter, setMaterialFilter] = useState('all');
  const [error, setError] = useState(null);

  // Form modal states
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [formMode, setFormMode] = useState('single'); // 'single', 'bulk', 'csv'
  const [editingItem, setEditingItem] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInventoryStats();
    
    // Subscribe to real-time inventory updates
    const unsubscribe = subscribeToInventoryItems((items) => {
      setInventoryItems(items);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load inventory statistics
  const loadInventoryStats = async () => {
    try {
      const inventoryStats = await getInventoryStats();
      setStats(inventoryStats);
    } catch (error) {
      console.error('Error loading inventory stats:', error);
      setError('Failed to load inventory statistics');
    }
  };

  // Handle form actions
  const handleAddItem = (mode = 'single') => {
    setFormMode(mode);
    setEditingItem(null);
    setShowInventoryForm(true);
  };

  const handleEditItem = (item) => {
    setFormMode('single');
    setEditingItem(item);
    setShowInventoryForm(true);
  };

  const handleDeleteItem = async (item) => {
    try {
      await deleteInventoryItem(item.id);
      message.success('Inventory item deleted successfully');
      loadInventoryStats(); // Refresh stats
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      message.error('Failed to delete inventory item');
    }
  };

  const handleFormSuccess = () => {
    setShowInventoryForm(false);
    setEditingItem(null);
    loadInventoryStats(); // Refresh stats
  };

  const handleFormCancel = () => {
    setShowInventoryForm(false);
    setEditingItem(null);
  };

  // Filter items based on search and filters
  useEffect(() => {
    let filtered = inventoryItems;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.materialType?.toLowerCase().includes(searchLower) ||
        item.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter - now based on availability calculations
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => {
        const availableQty = (item.newQuantity || 0) + (item.usedQuantity || 0);
        const damagedQty = item.damagedQuantity || 0;
        
        if (statusFilter === 'available' && availableQty > 0) return true;
        if (statusFilter === 'damaged' && damagedQty > 0) return true;
        if (statusFilter === 'low_stock' && availableQty < 10) return true;
        return false;
      });
    }

    // Apply material type filter
    if (materialFilter !== 'all') {
      filtered = filtered.filter(item => item.materialType === materialFilter);
    }

    setFilteredItems(filtered);
  }, [inventoryItems, searchTerm, statusFilter, materialFilter]);

  // Helper function to calculate availability status
  const getAvailabilityStatus = (item) => {
    const availableQty = (item.newQuantity || 0) + (item.usedQuantity || 0);
    const totalQty = item.totalQuantity || 0;
    const damagedQty = item.damagedQuantity || 0;
    
    if (totalQty === 0) return { status: 'empty', color: '#d9d9d9', text: 'Empty' };
    if (damagedQty > 0 && availableQty === 0) return { status: 'unavailable', color: '#ff4d4f', text: 'All Damaged' };
    if (availableQty === 0) return { status: 'unavailable', color: '#ff4d4f', text: 'Unavailable' };
    if (availableQty < totalQty * 0.2) return { status: 'low', color: '#faad14', text: 'Low Stock' };
    return { status: 'available', color: '#52c41a', text: 'Available' };
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Material Type',
      dataIndex: 'materialType',
      key: 'materialType',
      sorter: (a, b) => a.materialType.localeCompare(b.materialType),
      render: (text) => <Text strong>{text}</Text>,
      width: 200
    },
    {
      title: 'Total',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      sorter: (a, b) => (a.totalQuantity || 0) - (b.totalQuantity || 0),
      render: (qty) => <Text strong style={{ color: '#1890ff' }}>{qty || 0}</Text>,
      width: 100,
      align: 'center'
    },
    {
      title: 'New',
      dataIndex: 'newQuantity',
      key: 'newQuantity',
      sorter: (a, b) => (a.newQuantity || 0) - (b.newQuantity || 0),
      render: (qty) => (
        <Tag color="#52c41a" style={{ minWidth: '50px', textAlign: 'center' }}>
          {qty || 0}
        </Tag>
      ),
      width: 100,
      align: 'center'
    },
    {
      title: 'Used',
      dataIndex: 'usedQuantity',
      key: 'usedQuantity',
      sorter: (a, b) => (a.usedQuantity || 0) - (b.usedQuantity || 0),
      render: (qty) => (
        <Tag color="#faad14" style={{ minWidth: '50px', textAlign: 'center' }}>
          {qty || 0}
        </Tag>
      ),
      width: 100,
      align: 'center'
    },
    {
      title: 'Damaged',
      dataIndex: 'damagedQuantity',
      key: 'damagedQuantity',
      sorter: (a, b) => (a.damagedQuantity || 0) - (b.damagedQuantity || 0),
      render: (qty) => (
        <Tag color="#ff4d4f" style={{ minWidth: '50px', textAlign: 'center' }}>
          {qty || 0}
        </Tag>
      ),
      width: 110,
      align: 'center'
    },
    {
      title: 'Available',
      key: 'available',
      sorter: (a, b) => {
        const aAvailable = (a.newQuantity || 0) + (a.usedQuantity || 0);
        const bAvailable = (b.newQuantity || 0) + (b.usedQuantity || 0);
        return aAvailable - bAvailable;
      },
      render: (_, record) => {
        const available = (record.newQuantity || 0) + (record.usedQuantity || 0);
        return (
          <Text strong style={{ color: available > 0 ? '#52c41a' : '#ff4d4f' }}>
            {available}
          </Text>
        );
      },
      width: 110,
      align: 'center'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const status = getAvailabilityStatus(record);
        return (
          <Tag color={status.color}>
            {status.text}
          </Tag>
        );
      },
      width: 120,
      align: 'center'
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purchaseDate',
      key: 'purchaseDate',
      sorter: (a, b) => {
        const dateA = a.purchaseDate ? new Date(a.purchaseDate) : new Date(0);
        const dateB = b.purchaseDate ? new Date(b.purchaseDate) : new Date(0);
        return dateA - dateB;
      },
      render: (date) => date ? new Date(date).toLocaleDateString() : <Text type="secondary">N/A</Text>,
      width: 120
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text>{text || <Text type="secondary">No notes</Text>}</Text>
        </Tooltip>
      ),
      width: 150
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record)}
          >
            Edit
          </Button>
          <Button 
            size="small" 
            type="link" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(record)}
          >
            Delete
          </Button>
        </Space>
      ),
      width: 120,
      fixed: 'right'
    }
  ];


  if (error) {
    return (
      <div className="inventory-dashboard">
        <Alert
          message="Error Loading Inventory"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="inventory-dashboard">
      <div className="dashboard-header">
        <Title level={2}>Inventory Management</Title>
        <Space>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="single" onClick={() => handleAddItem('single')}>
                  Add Single Item
                </Menu.Item>
                <Menu.Item key="bulk" onClick={() => handleAddItem('bulk')}>
                  Add Multiple Items
                </Menu.Item>
                <Menu.Item key="csv" onClick={() => handleAddItem('csv')}>
                  Import from CSV
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button type="primary" icon={<PlusOutlined />}>
              Add Item <DownOutlined />
            </Button>
          </Dropdown>
          <Button 
            icon={<ReloadOutlined />}
            onClick={loadInventoryStats}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Overall Statistics */}
      <Row gutter={16} className="stats-row">
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Items"
              value={stats.totalQuantity || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Available Items"
              value={stats.availableQuantity || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="New Items"
              value={stats.newQuantity || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Damaged Items"
              value={stats.damagedQuantity || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Scaffold Availability Selector - Replaces redundant Material Type Statistics */}
      <ScaffoldAvailabilitySelector 
        onSelectionChange={(data) => {
          console.log('Scaffold selection changed:', data);
          // Handle scaffold selection changes if needed
        }}
      />

      {/* Inventory Items Table */}
      <Card 
        title="Inventory Items" 
        className="inventory-table-card"
        extra={
          <Space>
            <Input
              placeholder="Search items..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140 }}
            >
              <Option value="all">All Status</Option>
              <Option value="available">Available</Option>
              <Option value="damaged">Has Damaged</Option>
              <Option value="low_stock">Low Stock</Option>
            </Select>
            <Select
              value={materialFilter}
              onChange={setMaterialFilter}
              style={{ width: 200 }}
              showSearch
              placeholder="All Materials"
            >
              <Option value="all">All Materials</Option>
              {Object.values(MATERIAL_TYPES).sort().map(materialType => (
                <Option key={materialType} value={materialType}>{materialType}</Option>
              ))}
            </Select>
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredItems}
            rowKey="id"
            pagination={{
              total: filteredItems.length,
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} items`
            }}
            scroll={{ x: 800 }}
          />
        </Spin>
      </Card>

      {/* Inventory Form Modal */}
      <InventoryForm
        visible={showInventoryForm}
        onCancel={handleFormCancel}
        onSuccess={handleFormSuccess}
        editingItem={editingItem}
        mode={formMode}
      />
    </div>
  );
};

export default InventoryDashboard;
